import React, { useState, useEffect, useRef, useContext } from "react";
import {
    makeStyles,
    TextField,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar,
    ClickAwayListener,
    CircularProgress,
    Badge
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";

import api from "../../services/api";
import socketConnection from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";
import Audio from "../../components/Audio";
import {
    normalizeFilesForUpload,
    normalizeFileForUpload,
} from "../../utils/upload";

const AUDIO_CONSTRAINTS = {
    audio: {
        sampleRate: { ideal: 48000 },
        sampleSize: { ideal: 16 },
        channelCount: { ideal: 1 },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
    },
};

const RECORDING_AUDIO_BITS_PER_SECOND = 128000;

const getBestRecordingMimeType = () => {
    if (
        typeof MediaRecorder === "undefined" ||
        typeof MediaRecorder.isTypeSupported !== "function"
    ) {
        return "";
    }
    if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        return "audio/ogg;codecs=opus";
    }
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        return "audio/webm;codecs=opus";
    }
    return "";
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        height: "calc(100vh - 68px)",
        backgroundColor: "#f0f2f5",
        overflow: "hidden",
        fontFamily: "Roboto, sans-serif",
        minHeight: 0,
    },
    sidebar: {
        width: 320,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
        minHeight: 0,
    },
    chatContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#e5ddd5",
        position: "relative",
        minHeight: 0,
    },
    chatHeader: { padding: "10px 16px", backgroundColor: "#ededed", borderBottom: "1px solid rgba(0, 0, 0, 0.1)", zIndex: 1 },
    messageList: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
        backgroundRepeat: "repeat",
    },
    dateDivider: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "20px 0",
        "& span": {
            backgroundColor: "#fff",
            color: "#54656f",
            padding: "5px 12px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: "bold",
            textTransform: "uppercase",
            boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)"
        }
    },
    messageBubble: {
        maxWidth: "70%",
        padding: "6px 8px 18px 8px",
        borderRadius: "7.5px",
        marginBottom: "4px",
        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
        fontSize: "14px",
        wordBreak: "break-word",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minWidth: 120,
    },
    fromMe: {
        alignSelf: "flex-end",
        backgroundColor: "#dcf8c6",
        borderTopRightRadius: 0,
    },
    fromOthers: {
        alignSelf: "flex-start",
        backgroundColor: "#ffffff",
        borderTopLeftRadius: 0,
    },
    messageTimestamp: {
        fontSize: "10px",
        color: "rgba(0, 0, 0, 0.45)",
        position: "absolute",
        right: 8,
        bottom: 4,
        lineHeight: 1,
    },
    inputArea: { padding: "10px 15px", backgroundColor: "#f0f0f0", display: "flex", alignItems: "center", position: "relative", zIndex: 2 },
    emojiBox: { position: "absolute", bottom: "63px", left: "10px", zIndex: 999 },
    userName: { fontSize: "11px", fontWeight: "bold", marginBottom: 2 },
    hiddenInput: { display: "none" },
    audioPlayer: {
        marginTop: 4,
        width: "100%",
        minWidth: 250,
        maxWidth: 320,
        borderRadius: 8,
        display: "block",
    },
    mediaMessageTimestamp: {
        position: "static",
        alignSelf: "flex-end",
        marginTop: 4,
    },
    mediaImage: {
        marginTop: 4,
        width: "100%",
        maxWidth: 300,
        borderRadius: 8,
        display: "block",
        objectFit: "cover",
        cursor: "pointer",
    },
    mediaDocument: {
        marginTop: 4,
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.05)",
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid rgba(0,0,0,0.08)",
        maxWidth: 300,
    },
    mediaDocumentText: {
        flex: 1,
        marginRight: 8,
        fontWeight: 500,
    },
    quotedContainer: { backgroundColor: "rgba(0,0,0,0.06)", borderLeft: "4px solid #075e54", padding: "5px 8px", marginBottom: 4, borderRadius: 4, cursor: "pointer" },
    messageText: {
        whiteSpace: "pre-wrap",
        paddingRight: 52,
    },
    deleteButton: {
        position: "absolute",
        top: 2,
        right: 2,
        opacity: 0.65,
        "&:hover": {
            opacity: 1,
        },
    },
    recordingTimerWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fff",
        borderRadius: 20,
        padding: "0 10px",
        marginRight: 8,
        height: 40,
        minWidth: 84,
        "& span": {
            fontWeight: 600,
            fontSize: 14,
        },
    },
    replyPreviewContainer: { background: "#eee", padding: "8px 12px", borderLeft: "6px solid #075e54", display: "flex", alignItems: "center" }
}));

const InternalChat = () => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const [queues, setQueues] = useState([]);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [showEmoji, setShowEmoji] = useState(false);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [replyingMessage, setReplyingMessage] = useState(null);
    const [unreadMessages, setUnreadMessages] = useState({});

    const scrollRef = useRef();
    const fileInputRef = useRef();
    const recorderRef = useRef(null);
    const recordingStreamRef = useRef(null);
    const recordingChunksRef = useRef([]);
    const previousMessagesLengthRef = useRef(0);
    const socketRef = useRef(null);
    const selectedQueueRef = useRef(null);

    // Helpers de Formatação e Agrupamento
    const getUserColor = (userId) => {
        if (!userId) return "#757575";
        const hash = String(userId).split("").reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        return `hsl(${Math.abs(hash) % 360}, 70%, 40%)`;
    };

    const formatMessageTime = (dateString) => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
        } catch (e) { return ""; }
    };

    const groupMessagesByDate = (messages) => {
        const groups = {};
        messages.forEach((msg) => {
            const date = new Date(msg.createdAt).toLocaleDateString('pt-BR');
            if (!groups[date]) groups[date] = [];
            groups[date].push(msg);
        });
        return groups;
    };

    const getLabelDate = (dateStr) => {
        const today = new Date().toLocaleDateString('pt-BR');
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toLocaleDateString('pt-BR');

        if (dateStr === today) return "Hoje";
        if (dateStr === yesterdayStr) return "Ontem";
        return dateStr;
    };

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const { data } = await api.get(`/users/${user.id}`);
                setQueues(data.queues);
            } catch (err) { toast.error("Erro ao carregar setores"); }
        };
        fetchQueues();
    }, [user.id]);

    useEffect(() => {
        selectedQueueRef.current = selectedQueue;
    }, [selectedQueue]);

    const fetchMessages = async (queueId) => {
        if (!queueId) return;
        try {
            const { data } = await api.get(`/internal-messages/${queueId}`);
            setMessages(Array.isArray(data) ? data : []);
        } catch (err) {
            setMessages([]);
        }
    };

    useEffect(() => {
        const upsertMessage = (incomingMessage) => {
            if (!incomingMessage?.id) return;
            setMessages((prev) => {
                const messageIndex = prev.findIndex((msg) => msg.id === incomingMessage.id);
                if (messageIndex !== -1) {
                    const next = [...prev];
                    next[messageIndex] = { ...next[messageIndex], ...incomingMessage };
                    return next;
                }
                return [...prev, incomingMessage];
            });
        };

        const socket = socketConnection();
        socketRef.current = socket;

        const joinQueueRooms = () => {
            queues.forEach((q) => socket.emit("joinChatBox", `queue-${q.id}-notification`));
        };

        socket.on("connect", joinQueueRooms);
        joinQueueRooms();

        socket.on("appMessage", (data) => {
            if (data.action === "create") {
                const msgQueueId = Number(data.message?.queueId);
                const currentSelectedQueueId = Number(selectedQueueRef.current?.id);

                if (currentSelectedQueueId && msgQueueId === currentSelectedQueueId) {
                    upsertMessage(data.message);
                } else if (msgQueueId) {
                    setUnreadMessages((prev) => ({ ...prev, [msgQueueId]: (prev[msgQueueId] || 0) + 1 }));
                }
            }

            if (data.action === "delete") {
                const msgQueueId = Number(data.queueId);
                const currentSelectedQueueId = Number(selectedQueueRef.current?.id);
                if (currentSelectedQueueId && msgQueueId === currentSelectedQueueId) {
                    setMessages((prev) => prev.filter((msg) => msg.id !== data.messageId));
                }
            }
        });

        return () => {
            queues.forEach((q) => socket.emit("leaveChatBox", `queue-${q.id}-notification`));
            socket.off("connect", joinQueueRooms);
            socket.off("appMessage");
            socket.disconnect();
            socketRef.current = null;
        };
    }, [queues]);

    useEffect(() => {
        if (!selectedQueue) return;
        fetchMessages(selectedQueue.id);
    }, [selectedQueue]);

    useEffect(() => {
        return () => {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
            if (recordingStreamRef.current) {
                recordingStreamRef.current.getTracks().forEach((track) => track.stop());
                recordingStreamRef.current = null;
            }
        };
    }, []);

    const isNearBottom = () => {
        if (!scrollRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        return scrollHeight - (scrollTop + clientHeight) < 120;
    };

    const scrollToBottom = (behavior = "auto") => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior,
        });
    };

    useEffect(() => {
        if (!scrollRef.current) return;

        const isFirstLoad = previousMessagesLengthRef.current === 0;
        const hasNewMessage = messages.length > previousMessagesLengthRef.current;
        const shouldAutoScroll = isFirstLoad || isNearBottom();

        if (hasNewMessage && shouldAutoScroll) {
            scrollToBottom(isFirstLoad ? "auto" : "smooth");
        }

        previousMessagesLengthRef.current = messages.length;
    }, [messages]);

    const handleSelectQueue = (queue) => {
        setSelectedQueue(queue);
        setUnreadMessages((prev) => ({ ...prev, [queue.id]: 0 }));
        previousMessagesLengthRef.current = 0;
    };

    const handleAddEmoji = (e) => { setContent(prev => prev + e.native); };

    const handleFileUpload = async (e) => {
        const files = e.target.files;
        if (!files || !selectedQueue) return;
        setLoadingMedia(true);
        try {
            const normalizedFiles = await normalizeFilesForUpload(Array.from(files));
            const formData = new FormData();
            formData.append("queueId", selectedQueue.id);
            if (replyingMessage) formData.append("quotedMsgId", replyingMessage.id);
            normalizedFiles.forEach(file => formData.append("medias", file));
            await api.post("/internal-messages/media", formData);
            await fetchMessages(selectedQueue.id);
            setReplyingMessage(null);
        } catch (err) { toast.error(err?.message || "Erro ao enviar arquivo"); }
        setLoadingMedia(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleStartRecording = async () => {
        if (!selectedQueue || loadingMedia) return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            toast.error("Gravação de áudio não suportada neste navegador.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
            recordingStreamRef.current = stream;
            const mimeType = getBestRecordingMimeType();
            const recorderOptions = {
                audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
            };

            if (mimeType) {
                recorderOptions.mimeType = mimeType;
            }

            recorderRef.current = new MediaRecorder(stream, recorderOptions);
            recordingChunksRef.current = [];
            recorderRef.current.ondataavailable = (e) => {
                if (e.data?.size > 0) {
                    recordingChunksRef.current.push(e.data);
                }
            };
            recorderRef.current.onstop = async () => {
                const blobType = mimeType || "audio/webm";
                const blob = new Blob(recordingChunksRef.current, { type: blobType });
                recordingChunksRef.current = [];
                setLoadingMedia(true);
                try {
                    const extension = blobType.includes("ogg") ? "ogg" : "webm";
                    const audioFile = await normalizeFileForUpload(
                        new File([blob], `audio_interno_${Date.now()}.${extension}`, { type: blobType })
                    );
                    const formData = new FormData();
                    formData.append("queueId", selectedQueue.id);
                    formData.append("medias", audioFile);
                    if (replyingMessage) formData.append("quotedMsgId", replyingMessage.id);
                    await api.post("/internal-messages/media", formData);
                    await fetchMessages(selectedQueue.id);
                    setReplyingMessage(null);
                } catch (err) { toast.error("Erro ao enviar áudio"); }
                finally {
                    setLoadingMedia(false);
                    setIsRecording(false);
                    if (recordingStreamRef.current) {
                        recordingStreamRef.current.getTracks().forEach(track => track.stop());
                        recordingStreamRef.current = null;
                    }
                    recorderRef.current = null;
                }
            };
            recorderRef.current.start(250);
            setIsRecording(true);
        } catch (err) {
            toast.error("Falha no microfone.");
            setIsRecording(false);
        }
    };

    const handleSend = async () => {
        if (!content.trim() || !selectedQueue) return;
        try {
            const { data } = await api.post("/internal-messages", {
                body: content,
                queueId: selectedQueue.id,
                quotedMsgId: replyingMessage?.id
            });
            if (data?.id) {
                setMessages((prev) => {
                    const exists = prev.some((msg) => msg.id === data.id);
                    return exists ? prev : [...prev, data];
                });
            } else {
                await fetchMessages(selectedQueue.id);
            }
            setContent("");
            setReplyingMessage(null);
            setShowEmoji(false);
        } catch (err) { toast.error("Erro ao enviar mensagem"); }
    };

    const handleDeleteMessage = async (messageId) => {
        try {
            await api.delete(`/internal-messages/${messageId}`);
            setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        } catch (err) {
            toast.error("Erro ao excluir mensagem");
        }
    };

    const renderMessageMedia = (msg) => {
        const mediaUrl = msg.mediaUrl || "";
        const isImage = msg.mediaType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);

        if (msg.mediaType === "audio") {
            return <Audio url={mediaUrl} />;
        }

        if (isImage) {
            return (
                <img
                    src={mediaUrl}
                    alt="mídia"
                    className={classes.mediaImage}
                    onClick={() => window.open(mediaUrl, "_blank")}
                />
            );
        }

        return (
            <div className={classes.mediaDocument}>
                <Typography variant="caption" noWrap className={classes.mediaDocumentText}>
                    {mediaUrl.split("-").pop()}
                </Typography>
                <IconButton size="small" color="primary" component="a" href={mediaUrl} download target="_blank">
                    <GetAppIcon fontSize="small" />
                </IconButton>
            </div>
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.sidebar}>
                <Typography variant="h6" style={{ padding: 18, fontWeight: 700 }}>Comunicação Interna</Typography>
                <Divider />
                <List style={{ overflowY: "auto" }}>
                    {queues.map((q) => (
                        <ListItem button key={q.id} selected={selectedQueue?.id === q.id} onClick={() => handleSelectQueue(q)}>
                            <Badge badgeContent={unreadMessages[q.id]} color="secondary" overlap="circular">
                                <Avatar style={{ backgroundColor: q.color || "#ccc", marginRight: 12 }}>{q.name.charAt(0).toUpperCase()}</Avatar>
                            </Badge>
                            <ListItemText
                                primary={q.name}
                                secondary={q.description}
                                primaryTypographyProps={{ style: { fontWeight: unreadMessages[q.id] > 0 ? 'bold' : 'normal' } }}
                            />
                        </ListItem>
                    ))}
                </List>
            </div>

            <div className={classes.chatContainer}>
                {selectedQueue ? (
                    <>
                        <div className={classes.chatHeader}>
                            <Typography variant="subtitle1" style={{ fontWeight: 700, padding: 15 }}>#{selectedQueue.name} - {selectedQueue.description}</Typography>
                        </div>

                        <div className={classes.messageList} ref={scrollRef}>
                            {Object.entries(groupMessagesByDate(messages)).map(([date, msgs]) => (
                                <React.Fragment key={date}>
                                    <div className={classes.dateDivider}>
                                        <span>{getLabelDate(date)}</span>
                                    </div>
                                    {msgs.map((msg, i) => {
                                        const isMe = msg.userId === user?.id;
                                        const userColor = getUserColor(msg.userId);

                                        return (
                                            <div key={msg.id || i}
                                                className={`${classes.messageBubble} ${isMe ? classes.fromMe : classes.fromOthers}`}
                                                style={!isMe ? { borderLeftColor: userColor } : {}}
                                                onDoubleClick={() => setReplyingMessage(msg)}
                                            >
                                                {isMe && (
                                                    <IconButton
                                                        size="small"
                                                        className={classes.deleteButton}
                                                        onClick={() => handleDeleteMessage(msg.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {!isMe && (
                                                    <div className={classes.userName} style={{ color: userColor }}>
                                                        {msg.user?.name || "Colega"}
                                                    </div>
                                                )}
                                                {msg.quotedMsg && (
                                                    <div className={classes.quotedContainer}>
                                                        <Typography variant="caption" style={{ fontWeight: "bold", color: "#075e54" }}>
                                                            {msg.quotedMsg.user?.name}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" noWrap>
                                                            {msg.quotedMsg.body}
                                                        </Typography>
                                                    </div>
                                                )}
                                                {msg.mediaUrl ? (
                                                    renderMessageMedia(msg)
                                                ) : (
                                                    <Typography variant="body2" className={classes.messageText}>{msg.body}</Typography>
                                                )}
                                                <span className={`${classes.messageTimestamp} ${msg.mediaUrl ? classes.mediaMessageTimestamp : ""}`}>
                                                    {formatMessageTime(msg.createdAt)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>

                        {replyingMessage && (
                            <div className={classes.replyPreviewContainer}>
                                <div style={{ flex: 1 }}>
                                    <Typography variant="caption" style={{ color: "#075e54", fontWeight: "bold" }}>Respondendo a {replyingMessage.user?.name}</Typography>
                                    <Typography variant="body2" noWrap>{replyingMessage.body}</Typography>
                                </div>
                                <IconButton size="small" onClick={() => setReplyingMessage(null)}><CloseIcon fontSize="small" /></IconButton>
                            </div>
                        )}

                        <div className={classes.inputArea}>
                            <IconButton onClick={() => setShowEmoji(!showEmoji)}><InsertEmoticonIcon /></IconButton>
                            <input type="file" multiple className={classes.hiddenInput} ref={fileInputRef} onChange={handleFileUpload} />
                            <IconButton onClick={() => fileInputRef.current.click()} disabled={loadingMedia || isRecording}>
                                {loadingMedia ? <CircularProgress size={24} /> : <AttachFileIcon />}
                            </IconButton>
                            {isRecording && (
                                <div className={classes.recordingTimerWrapper}>
                                    <RecordingTimer />
                                </div>
                            )}
                            <TextField
                                fullWidth variant="outlined" size="small" value={content}
                                placeholder={isRecording ? "Gravando..." : "Escreva sua mensagem..."}
                                disabled={isRecording}
                                onChange={(e) => setContent(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSend()}
                                style={{ backgroundColor: "#fff", borderRadius: 20 }}
                            />
                            {content.trim() ? (
                                <IconButton color="primary" onClick={handleSend}><SendIcon /></IconButton>
                            ) : (
                                <IconButton
                                    color={isRecording ? "secondary" : "primary"}
                                    onClick={isRecording ? () => recorderRef.current?.stop() : handleStartRecording}
                                >
                                    {isRecording ? <StopIcon /> : <MicIcon />}
                                </IconButton>
                            )}
                        </div>
                        {showEmoji && (
                            <div className={classes.emojiBox}>
                                <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                                    <Picker perLine={8} showPreview={false} onSelect={handleAddEmoji} set="apple" />
                                </ClickAwayListener>
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="h5" style={{ opacity: 0.5 }}>Selecione um canal para conversar!</Typography>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InternalChat;
